import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Twitter, Facebook, Instagram, Youtube, Github, Globe } from "lucide-react";
import { Logo } from "@/components/site/Header";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SICON = { linkedin: Linkedin, twitter: Twitter, facebook: Facebook, instagram: Instagram, youtube: Youtube, github: Github, globe: Globe };

function LegalLink({ doc, path, fallback, testid, onPopup }) {
  if (!doc || doc.enabled === false) return null;
  const title = doc.title || fallback;
  if (doc.mode === "popup") return <button onClick={() => onPopup({ title, body: doc.body })} className="hover:text-white" data-testid={testid}>{title}</button>;
  return <Link to={path} className="hover:text-white" data-testid={testid}>{title}</Link>;
}

export default function Footer({ content }) {
  const [popup, setPopup] = useState(null);
  const c = content?.contact || {};
  const services = (content?.services || []).map((s) => s.title);
  return (
    <footer className="bg-navy text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="space-y-4">
          <Logo light />
          <p className="text-sm text-slate-400 leading-relaxed">{content?.footer_tagline || "Connecting exceptional talent with growing businesses across India."}</p>
          <div className="flex gap-2">
            {(content?.footer_socials && content.footer_socials.length ? content.footer_socials : [{ id: "li", url: c.linkedin, icon: "linkedin" }]).map((s) => {
              const I = SICON[s.icon] || Globe;
              return <a key={s.id} href={s.url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white/10 hover:bg-emerald-brand transition-colors"><I size={16} /></a>;
            })}
          </div>
        </div>
        <div>
          <h4 className="text-white font-display font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-emerald-light transition-colors">Home</Link></li>
            <li><Link to="/hire-talent" className="hover:text-emerald-light transition-colors">Hire Talent</Link></li>
            <li><Link to="/submit-resume" className="hover:text-emerald-light transition-colors">Submit Resume</Link></li>
            <li><Link to="/contact" className="hover:text-emerald-light transition-colors">Contact Us</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-display font-semibold mb-4">Services</h4>
          <ul className="space-y-2 text-sm">
            {services.slice(0, 6).map((s) => <li key={s} className="text-slate-400">{s}</li>)}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-display font-semibold mb-4">Contact Info</h4>
          <ul className="space-y-3 text-sm">
            {(c.phones || []).map((p) => <li key={p} className="flex items-center gap-2"><Phone size={14} className="text-emerald-light" /> {p}</li>)}
            <li className="flex items-center gap-2"><Mail size={14} className="text-emerald-light" /> {c.email}</li>
            <li className="flex items-start gap-2"><MapPin size={14} className="text-emerald-light mt-0.5" /> {c.address}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span data-testid="footer-copyright">{(content?.footer_copyright || "© {year} Ishaan Workforce Solutions. All rights reserved.").replace("{year}", new Date().getFullYear())}</span>
          <div className="flex gap-5">
            <LegalLink doc={content?.legal_privacy} path="/privacy" fallback="Privacy Policy" testid="footer-privacy-link" onPopup={setPopup} />
            <LegalLink doc={content?.legal_terms} path="/terms" fallback="Terms & Conditions" testid="footer-terms-link" onPopup={setPopup} />
          </div>
        </div>
      </div>
      <Dialog open={!!popup} onOpenChange={(o) => !o && setPopup(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="legal-popup">
          <DialogHeader><DialogTitle className="font-display text-navy">{popup?.title}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{popup?.body}</div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
